import { Typography } from '@material-ui/core';
import { Check } from 'lucide-react';
import React from 'react';
import { CampaignManagementMockup } from '../../components/AmplifyProductMockups/CampaignManagementMockup';
import { CrossCampaignReportMockup } from '../../components/AmplifyProductMockups/CrossCampaignReportMockup';
import { LinkGeneratorMockup } from '../../components/AmplifyProductMockups/LinkGeneratorMockup';
import { YieldOptimizerMockup } from '../../components/AmplifyProductMockups/YieldOptimizerMockup';
import { DemoButton } from '../../components/DemoButton';
import { Headline } from '../../components/Headline';
import { mobileLeadText } from '../../components/MarketingTypography';
import { Reveal } from '../../components/motion';
import {
  JSPoweredBadge,
  NewBadge,
  NoCodeBadge
} from '../../components/NewBadge';
import PageHead from '../../components/PageHead';
import { SEO } from '../../components/SEO';
import MainLayout from '../../layouts/main';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const TiersGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  align-items: stretch;
  max-width: 780px;
  margin: 0 auto 80px;
  padding: 0 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const TierCard = styled('div')`
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.12);
    transform: translateY(-2px);
  }
`;

const TierBadgeRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const TierTitle = styled('h3')`
  font-size: 22px;
  font-weight: 700;
  color: #0e1c2b;
  margin: 0;
`;

const TierDescription = styled('p')`
  font-size: 15px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;
`;

const TierSetup = styled('div')`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
`;

const TierList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const TierItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const TierCta = styled('div')`
  margin-top: auto;
  padding-top: 14px;
`;

const LifecycleSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
`;

const LifecycleGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 48px;
  align-items: stretch;

  > * {
    height: 100%;
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const LifecycleStep = styled('div')`
  background-color: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  height: 100%;
  box-sizing: border-box;

  .step {
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
    margin-bottom: 10px;
  }

  h3 {
    font-size: 17px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0 0 8px;
  }

  p {
    font-size: 14px;
    line-height: 1.6;
    color: #4f4f4f;
    margin: 0;
  }
`;

const ViewSection = styled('section')<{ flip?: boolean }>`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: center;
  direction: ${(p) => (p.flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
    direction: ltr;
  }
`;

const ViewContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const ViewBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
`;

const ReportSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 0 24px;
`;

const ReportHeader = styled('div')`
  text-align: center;
  max-width: 680px;
  margin: 0 auto 40px;
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 120px;
  padding: 0 24px;
`;

const FinalCtaInner = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const FinalCtaBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 0;
  ${mobileLeadText}
`;

export default () => {
  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/product/amplify/"
          title="Affilimate Amplify: Campaign Infrastructure for Publishers"
          description="Sponsored content campaigns and link swap campaigns. Pitch, flight, and prove every deal on one platform, without adding headcount."
        />
        <PageHead style={{ marginBottom: 0 }}>
          <Reveal>
            <NewBadge>New</NewBadge>
            <Headline
              variant="h1"
              gutterBottom
              component="h1"
              style={{
                maxWidth: '800px',
                margin: '12px auto 24px',
                fontWeight: 900,
                display: 'block'
              }}
            >
              Campaign infrastructure that works the way your team does
            </Headline>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ maxWidth: '620px', margin: '24px auto 48px' }}
            >
              Sell sponsorship campaigns, offer existing links as programmable
              inventory, or run both in one deal. Amplify is campaign
              infrastructure for publishers from creator to enterprise scale.
            </Typography>
          </Reveal>
        </PageHead>

        <TiersGrid>
          <Reveal style={{ height: '100%' }}>
            <TierCard>
              <TierBadgeRow>
                <NoCodeBadge />
              </TierBadgeRow>
              <TierTitle>Sponsored content campaigns</TierTitle>
              <TierSetup>No dev work required</TierSetup>
              <TierDescription>
                Package new content, inclusions, and placements into
                sponsorships brands fund, with one report for every campaign
                instead of stitching SubIDs together in spreadsheets.
              </TierDescription>
              <TierList>
                <TierItem>
                  <Check size={16} />
                  See in-flight delivery while campaigns are still running
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Pitch with evidence from your own page and link history
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Offer campaigns on a CPA, CPC, or flat spend basis
                </TierItem>
              </TierList>
              <TierCta>
                <DemoButton text="Get a demo" variant="contained" />
              </TierCta>
            </TierCard>
          </Reveal>
          <Reveal delay={120} style={{ height: '100%' }}>
            <TierCard>
              <TierBadgeRow>
                <JSPoweredBadge />
              </TierBadgeRow>
              <TierTitle>
                Link swap campaigns &amp; yield optimization
              </TierTitle>
              <TierSetup>One-time on-page integration</TierSetup>
              <TierDescription>
                Expand your sellable inventory by swapping existing product
                links to higher-yield merchants across your whole portfolio,
                scoped by AI and approved by you.
              </TierDescription>
              <TierList>
                <TierItem>
                  <Check size={16} />
                  Turn pages you already publish into new revenue streams
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Portfolio-wide execution your team approves before anything
                  goes live
                </TierItem>
                <TierItem>
                  <Check size={16} />
                  Runs on the link and performance data Affilimate already
                  measures across your sites
                </TierItem>
              </TierList>
              <TierCta>
                <DemoButton text="Get a demo" variant="contained" />
              </TierCta>
            </TierCard>
          </Reveal>
        </TiersGrid>

        <LifecycleSection>
          <Reveal>
            <div style={{ textAlign: 'center' }}>
              <Headline component="h2">
                Pitch → Flight → Protect → Renew
              </Headline>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ maxWidth: '640px', margin: '16px auto 0' }}
              >
                Multiple deal structures, unified campaign infrastructure.
              </Typography>
            </div>
          </Reveal>
          <LifecycleGrid>
            <Reveal>
              <LifecycleStep>
                <div className="step">01 · Pitch</div>
                <h3>Know what's sellable</h3>
                <p>
                  Historical page and link performance tells you what to sell,
                  how to price it, and where swappable link inventory could
                  strengthen the deal and help you offer larger packages.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={100}>
              <LifecycleStep>
                <div className="step">02 · Flight</div>
                <h3>Customizable deal structures</h3>
                <p>
                  Multiple deal types, one delivery record. Performance is
                  measured the same way whether the deal is new placements,
                  swapped link inventory, or a combination.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={200}>
              <LifecycleStep>
                <div className="step">03 · Protect</div>
                <h3>Catch and react to issues in flight</h3>
                <p>
                  Track pacing against goal while the campaign is still running.
                  Catch underdelivery while there is still time to fix it,
                  without scrambling to assemble ad hoc reports.
                </p>
              </LifecycleStep>
            </Reveal>
            <Reveal delay={300}>
              <LifecycleStep>
                <div className="step">04 · Renew</div>
                <h3>Deliver and sell the next one</h3>
                <p>
                  One, clean campaign record from first tracked link to client
                  wrap report. Walk into the renewal with numbers you can back
                  up with historical performance.
                </p>
              </LifecycleStep>
            </Reveal>
          </LifecycleGrid>
        </LifecycleSection>

        <ViewSection>
          <Reveal>
            <ViewContent>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Find the money you're leaving on the table
              </Headline>
              <ViewBody>
                Many of your existing product links could earn more, but finding
                and switching them by hand does not scale. Yield optimization
                ranks the highest-upside swaps in your content and surfaces each
                opportunity for your team to review before anything launches.
              </ViewBody>
              <ViewBody>
                AI analysis surface higher-yield suggestions across your
                existing links, before you even open the platform. Then act on
                the spread in minutes instead of quarters.
              </ViewBody>
            </ViewContent>
          </Reveal>
          <Reveal delay={150}>
            <YieldOptimizerMockup />
          </Reveal>
        </ViewSection>

        <ViewSection flip>
          <Reveal>
            <ViewContent>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Built for lean commerce teams
              </Headline>
              <ViewBody>
                Our customers run commerce businesses without an army of
                analysts. Amplify replaces the cross-team work of digging
                through analytics, identifying links in content, comparing
                advertisers, and proposing campaigns with one streamlined
                workflow your existing team can own.
              </ViewBody>
              <ViewBody>
                Easily track pacing across your campaigns towards individual
                goals, without scrambling to assemble ad hoc reports.
              </ViewBody>
            </ViewContent>
          </Reveal>
          <Reveal delay={150}>
            <CampaignManagementMockup />
          </Reveal>
        </ViewSection>

        <ViewSection>
          <Reveal>
            <ViewContent>
              <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                Campaign links that track themselves
              </Headline>
              <ViewBody>
                Generate campaign links in seconds, so every interaction from
                click to purchase lands in your campaign report without manual
                tagging or cleanup. Ensure your team is using the right links
                for every campaign.
              </ViewBody>
            </ViewContent>
          </Reveal>
          <Reveal delay={150}>
            <LinkGeneratorMockup />
          </Reveal>
        </ViewSection>

        <ReportSection>
          <Reveal>
            <ReportHeader>
              <Headline component="h2" style={{ margin: '0 0 16px' }}>
                Cross-campaign visibility and pacing in seconds
              </Headline>
              <ViewBody>
                Spend, delivery, and extensive KPIs for every campaign in one
                report: no exports to stitch, no SubIDs to piece together, no
                spreadsheets to maintain.
              </ViewBody>
            </ReportHeader>
          </Reveal>
          <Reveal delay={150}>
            <CrossCampaignReportMockup />
          </Reveal>
        </ReportSection>

        <FinalCta>
          <Reveal>
            <FinalCtaInner>
              <Headline component="h2" style={{ margin: 0 }}>
                See Amplify on your campaigns
              </Headline>
              <FinalCtaBody>
                Walk through sponsorship flighting, delivery reporting, link
                swaps, and Yield optimization on a demo tailored to your
                portfolio and deal types.
              </FinalCtaBody>
              <DemoButton text="Get a demo" variant="contained" />
            </FinalCtaInner>
          </Reveal>
        </FinalCta>
      </div>
    </MainLayout>
  );
};
